import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { useAppSelector } from "../../../store/reducers";
import { BasketLineMinimal } from "../BasketLine";
import { ExpeditedShippingDisclosure } from "../ExpeditedShippingDisclosure";
import { SHIPPING_METHOD_NAMES, Step } from "../constants";
import { getLineOptionGroupingData } from "../selectors";

type IProps = Immutable<{
    onEdit?: (event?: React.MouseEvent<HTMLElement>) => void;
}>;

const buildLine = (lineURL: IBasketLineAPIURL, basket: IBasket | null) => {
    if (!basket) {
        return null;
    }
    const line = basket.lines.find((l) => {
        return l.url === lineURL;
    });
    if (!line || !line.quantity) {
        return null;
    }
    // Don't show bundle lines (any line which has children)
    const children = basket.lines.filter((l) => l.parent === line.id);
    if (children.length) {
        return;
    }
    const isBundled =
        basket.lines.filter((l) => l.id === line.parent).length > 0;

    return (
        <BasketLineMinimal
            key={`${lineURL}`}
            line={line}
            isBundled={isBundled}
        />
    );
};

export const ShippingMethodSummary = ({ onEdit }: IProps): React.ReactNode => {
    const form = useAppSelector((state) => state.checkout.form);
    const basket = useAppSelector((state) => state.checkout.data.basket);
    const groups = useAppSelector((state) =>
        getLineOptionGroupingData(state.checkout),
    );
    const showSummary = useAppSelector(
        (state) => state.checkout.form.current_step > Step.SHIPPING_METHOD,
    );

    const buildSummary = () => {
        if (!showSummary) {
            return null;
        }
        return groups.map((group, i) => {
            const method =
                group.methods.filter((m) => {
                    return form.shipping_method === m.code;
                })[0] || group.methods[0];

            const lines = group.lines.map((lineURL) =>
                buildLine(lineURL, basket),
            );
            return (
                <div key={i} className="checkout-step__shipping-group-summary">
                    {/* TODO(i18n): SHIPPING_METHOD_NAMES returns hardcoded strings - convert to function using t tagged template. Also wrap fallback method.name in t template: {t`${method.name}`} */}
                    <h3 className="checkout-step__label">
                        {SHIPPING_METHOD_NAMES[method.name] || method.name}
                    </h3>
                    {/* TODO(i18n): method.description comes from API - ensure backend wraps shipping method descriptions in gettext */}
                    <p>{method.description}</p>
                    <ul>{lines}</ul>
                </div>
            );
        });
    };

    const summaryClasses = classNames({
        "checkout-step__summary": true,
        "checkout-step__summary--collapsed": !showSummary,
    });

    if (groups.length <= 0) {
        return (
            <div className={summaryClasses}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className={summaryClasses}>
            {buildSummary()}
            <ExpeditedShippingDisclosure groups={groups} />
            {onEdit && (
                <p>
                    <a
                        className="checkout-step__back"
                        href="#"
                        onClick={onEdit}
                    >
                        {t`Edit`}
                    </a>
                </p>
            )}
        </div>
    );
};
